import { Component, OnInit, OnDestroy, ViewChild, ElementRef, ChangeDetectorRef } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { Location } from '@angular/common';
import { Subscription } from 'rxjs';
import { GlobalSearchService } from '../../services/globalsearchservice.service';
import { ReportsService } from '../../services/reports.service';
import { PrintService } from '../../services/print.service';

@Component({
	selector: 'app-sales-dashboard',
	templateUrl: './sales-dashboard.component.html',
	styleUrls: ['./sales-dashboard.component.scss'],
})
export class SalesDashboardComponent implements OnInit, OnDestroy {
	@ViewChild('printTable') printTableRef: ElementRef;

	color: string = 'blue';
	sending: boolean = false;
	results: any[] = [];
	year: number = new Date().getFullYear();
	lastyear: number = new Date().getFullYear() - 1;
	month: number = new Date().getMonth() + 1;

	// KPI summary cards derived from the Total row
	kpi: { totalSales: number; totalSalesLY: number; salesPct: number; totalYTD: number; totalYTDLY: number; ytdPct: number; activeCustomers: number; activeCustomersLY: number; } | null = null;

	yearRef        = new UntypedFormControl(new Date().getFullYear());
	monthRef       = new UntypedFormControl(new Date().getMonth() + 1);
	branchRef      = new UntypedFormControl('');
	salespersonRef = new UntypedFormControl([]);

	branches:   any[] = [];
	salespeople: any[] = [];
	years:      number[] = [];

	months = [
		{ value: 1,  label: 'January' },   { value: 2,  label: 'February' },
		{ value: 3,  label: 'March' },      { value: 4,  label: 'April' },
		{ value: 5,  label: 'May' },        { value: 6,  label: 'June' },
		{ value: 7,  label: 'July' },       { value: 8,  label: 'August' },
		{ value: 9,  label: 'September' },  { value: 10, label: 'October' },
		{ value: 11, label: 'November' },   { value: 12, label: 'December' },
	];

	sortColumn: string = 'salesytd';
	sortDir: 'asc' | 'desc' = 'desc';
	maxYtd: number = 0;

	private reportsub: Subscription;

	constructor(
		private globalSearchService: GlobalSearchService,
		private reportsService: ReportsService,
		private printService: PrintService,
		private location: Location,
		private cdr: ChangeDetectorRef
	) {
		this.color = this.globalSearchService.getColor();
		this.globalSearchService.mesagesallowed.next(false);
		this.globalSearchService.aiDataAvailable$.next(null);

		const currentYear = new Date().getFullYear();
		for (let y = currentYear; y >= currentYear - 5; y--) {
			this.years.push(y);
		}
	}

	ngOnInit(): void {
		this.reportsService.getBranches().subscribe((res: any) => {
			this.branches = Array.isArray(res) ? res : [];
			this.cdr.markForCheck();
		});
		this.reportsService.getSalesPeople().subscribe((res: any) => {
			this.salespeople = Array.isArray(res) ? res : [];
			this.cdr.markForCheck();
		});
	}

	runReport(): void {
		this.results = [];
		this.kpi     = null;
		this.sending = true;

		if (this.reportsub) { this.reportsub.unsubscribe(); }

		const selectedPeople: string[] = Array.isArray(this.salespersonRef.value) ? this.salespersonRef.value : [];
		const payload: any = {
			year:     this.yearRef.value,
			month:    this.monthRef.value,
			location: this.branchRef.value || '',
			salesman: '',
			manager:  '',
		};
		if (selectedPeople.length === 1) {
			payload.salesman = selectedPeople[0];
		} else if (selectedPeople.length > 1) {
			payload.manager = selectedPeople.map((c: string) => `'${c}'`).join(',');
		}

		this.reportsub = this.reportsService.getSalesDashboard(payload).subscribe({
			next: (r: any) => {
				this.sending = false;
				if (r && r.success) {
					this.year    = r.year;
					this.lastyear = r.lastyear;
					this.month   = r.month;
					this.results = Array.isArray(r.results) ? r.results : [];
					this._buildKpi();
					this._calcMaxYtd();
					this.globalSearchService.aiDataAvailable$.next(r);
				} else {
					this.results = [];
				}
				this.cdr.detectChanges();
			},
			error: () => {
				this.sending = false;
				this.results = [];
				this.cdr.detectChanges();
			},
		});
	}

	private _buildKpi(): void {
		const total = this.results.find(r => r.name === 'Total');
		if (!total) { this.kpi = null; return; }
		this.kpi = {
			totalSales:        total.sales      ?? 0,
			totalSalesLY:      total.salesly    ?? 0,
			salesPct:          total.salesdiffpercent ?? 0,
			totalYTD:          total.salesytd   ?? 0,
			totalYTDLY:        total.salesytdly ?? 0,
			ytdPct:            total.salesytddiff ?? 0,
			activeCustomers:   total.actives    ?? 0,
			activeCustomersLY: total.activesly  ?? 0,
		};
	}

	private _calcMaxYtd(): void {
		const brandRows = this.results.filter(r => !this.isTotal(r) && !this.isHidden(r) && r.name !== 'Colour');
		this.maxYtd = Math.max(...brandRows.map(r => Math.abs(r.salesytd ?? 0)), 1);
	}

	getBarWidth(row: any): number {
		if (!this.maxYtd || this.isTotal(row)) { return 0; }
		return Math.min(100, Math.round((Math.abs(row.salesytd ?? 0) / this.maxYtd) * 100));
	}

	sortBy(col: string): void {
		if (this.sortColumn === col) {
			this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
		} else {
			this.sortColumn = col;
			this.sortDir = 'desc';
		}
		const dir = this.sortDir === 'asc' ? 1 : -1;
		const total = this.results.find(r => r.name === 'Total');
		const rest  = this.results.filter(r => r.name !== 'Total' && !this.isHidden(r));
		rest.sort((a, b) => {
			const av = a[col] ?? 0;
			const bv = b[col] ?? 0;
			return (av < bv ? -1 : av > bv ? 1 : 0) * dir;
		});
		this.results = total ? [total, ...rest] : rest;
	}

	getSortIcon(col: string): string {
		if (this.sortColumn !== col) { return 'unfold_more'; }
		return this.sortDir === 'asc' ? 'arrow_upward' : 'arrow_downward';
	}

	getMonthName(monthNum: number): string {
		return this.months.find(x => x.value === monthNum)?.label ?? '';
	}

	getVisibleResults(): any[] {
		return this.results.filter(r => !this.isHidden(r));
	}

	formatCurrency(val: number): string {
		if (val == null || isNaN(val)) { return '$0.00'; }
		return val.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
	}

	formatCurrencyCompact(val: number): string {
		if (val == null || isNaN(val)) { return '$0'; }
		const abs = Math.abs(val);
		const sign = val < 0 ? '-' : '';
		if (abs >= 1_000_000) { return sign + '$' + (abs / 1_000_000).toFixed(1) + 'M'; }
		if (abs >= 1_000)     { return sign + '$' + (abs / 1_000).toFixed(1) + 'K'; }
		return sign + '$' + abs.toFixed(0);
	}

	formatPercent(val: number): string {
		if (val == null || isNaN(val)) { return '0.00%'; }
		const sign = val > 0 ? '+' : '';
		return sign + val.toFixed(2) + '%';
	}

	formatDiff(val: number): string {
		if (val == null || isNaN(val)) { return '0'; }
		const sign = val > 0 ? '+' : '';
		return sign + val;
	}

	isNegative(val: number): boolean { return val != null && !isNaN(val) && val < 0; }
	isPositive(val: number): boolean { return val != null && !isNaN(val) && val > 0; }
	isTotal(row: any): boolean       { return row?.name === 'Total'; }
	isHidden(row: any): boolean      { return row?.name === 'Discounted' || row?.name === 'Non-Discounted'; }

	trackByName(_i: number, row: any): string { return row?.name ?? String(_i); }

	exportXls(): void {
		this.sending = true;
		const now  = new Date();
		const data = {
			content:  this.globalSearchService.base64encode(this.generateExportHTML()),
			filename: `SalesDashboard_${this.yearRef.value}_${this.getMonthName(this.monthRef.value)}`,
			title:    `Sales Dashboard — ${this.getMonthName(this.monthRef.value)} ${this.yearRef.value} vs ${this.yearRef.value - 1}`,
			subtitle: `Generated ${now.toLocaleString()}`,
		};
		this.printService.xls(data).subscribe((result: any) => {
			this.sending = false;
			this.globalSearchService.downloadXls(result.content, data.filename);
		});
	}

	exportPdf(): void {
		this.sending = true;
		const now  = new Date();
		const data = {
			content:  this.globalSearchService.base64encode(this.generateExportHTML()),
			filename: `SalesDashboard_${this.yearRef.value}_${this.getMonthName(this.monthRef.value)}`,
			title:    `Sales Dashboard — ${this.getMonthName(this.monthRef.value)} ${this.yearRef.value} vs ${this.yearRef.value - 1}`,
			subtitle: `Generated ${now.toLocaleString()}`,
		};
		this.printService.pdf(data).subscribe((result: any) => {
			this.sending = false;
			this.globalSearchService.downloadPdf(result.content, data.filename);
		});
	}

	generateExportHTML(): string {
		const visible = this.getVisibleResults();
		const red = 'color:#c62828;'; const green = 'color:#2e7d32;';
		let html = `<table border="1" cellspacing="0" cellpadding="5"
			style="border-collapse:collapse;font-size:11px;font-family:Arial,sans-serif;">
			<thead><tr style="background:#1a237e;color:#fff;text-align:center;">
				<th style="text-align:left;padding:6px 10px;">Category</th>
				<th>Actives</th><th>Actives LY</th><th>+/-</th>
				<th>Sales</th><th>Sales LY</th><th>+/-</th><th>+/-%</th>
				<th>Sales YTD</th><th>Sales YTD LY</th><th>YTD +/-%</th>
			</tr></thead><tbody>`;
		visible.forEach(row => {
			const bg = this.isTotal(row) ? 'background:#e3f2fd;font-weight:700;' : '';
			html += `<tr style="${bg}">
				<td style="padding:4px 10px;">${row.name}</td>
				<td align="right">${row.actives}</td>
				<td align="right">${row.activesly}</td>
				<td align="right" style="${row.activesdiff < 0 ? red : ''}">${this.formatDiff(row.activesdiff)}</td>
				<td align="right">${this.formatCurrency(row.sales)}</td>
				<td align="right">${this.formatCurrency(row.salesly)}</td>
				<td align="right" style="${row.salesdiff < 0 ? red : row.salesdiff > 0 ? green : ''}">${this.formatCurrency(row.salesdiff)}</td>
				<td align="right" style="${row.salesdiffpercent < 0 ? red : row.salesdiffpercent > 0 ? green : ''}">${this.formatPercent(row.salesdiffpercent)}</td>
				<td align="right">${this.formatCurrency(row.salesytd)}</td>
				<td align="right">${this.formatCurrency(row.salesytdly)}</td>
				<td align="right" style="${row.salesytddiff < 0 ? red : row.salesytddiff > 0 ? green : ''}">${this.formatPercent(row.salesytddiff)}</td>
			</tr>`;
		});
		html += `</tbody></table>`;
		return html;
	}

	back(): void { this.location.back(); }

	ngOnDestroy(): void {
		if (this.reportsub) { this.reportsub.unsubscribe(); }
	}
}
